/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import { Address4, Address6 } from 'ip-address';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

import CommunicatorOrientation from '../orientation/communicator-orientation';
import ErrorLevel from '../../error/error-level';
import FileSystemType from '../../file/system/file-system-type';
import NetworkType from '../../network/network-type';
import UpdatePlatformUtil from '../../update/update-platform-util';
import StorageTypesParser from '../../session/storage-types-parser';
import config from '../../../config';

/**
 * Komunikátor informací o zařízení pro Ki-Wi Server ve formátu Status Info v1.2:
 * https://gitlab.com/ki-wi/server/kiwi-status-info/-/wikis/Specifikace-status-info-v1.0
 * @see {StatusInfo}
 *
 * TODO: Ne všechno co máme k dispozici umíme vrátit.
 * TODO: Možná některé pomocné funkce přemístit do StatusInfo?
 * TODO: Celé ReportInfo je nyní po refaktoringu zbytečné, jelikož předtím byl ReportInfo
 * cca ve stavu 1:1 posílaném na Ki-Wi Server. Problém je, že nyní jsou různé formáty
 * (např. formáty datumů podle specifikace) distribuované do více souborů, což je špatně
 * udržitelné. Jelikož se nyní ReportInfo využívá jen zde, tak by bylo dobré tyto soubory
 * logicky sloučit, aby to bylo na jednom místě a nebylo součastí zbytečného veřejného
 * rozhraní ReportInfo.
 */
class CommunicatorReport {
  /**
   * Escapování části klíče, aby neobsahoval oddělovač `.` nebo escapovací znak `\`.
   * Nejprve se nahradí výskyty `\` za `\\` a následně výskyty `.` za `\.`.
   * @param {string} key
   * @private
   * @returns {string}
   */
  static encodeKeyComponent(key) {
    return key.replaceAll('\\', '\\\\').replaceAll('.', '\\.');
  }

  /**
   * Odešle informace o zařízení.
   * @param {ReportInfo} reportInfo Informace o zařízení.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení, které chce komunikovat.
   * @param {string} serverUrl URL adresa Ki-Wi Server pro komunikaci.
   * @returns {Promise.<undefined>} Nic.
   */
  static fetch(reportInfo, deviceId, serverUrl) {
    return (
      fetch(`${serverUrl}/api/device/${deviceId}/status`, {
        body: JSON.stringify(this.generateReport(reportInfo, serverUrl)),
        headers: { 'Content-type': 'application/json' },
        method: 'POST'
      })
        // Snížení priority logování pro chyby síťového spojení.
        .catch((error) => {
          error.level = ErrorLevel.Warning;
          throw error;
        })
        .then((response) => {
          // Snížení priority logování pro chyby ze serveru.
          if (!response.ok) {
            const error = new RangeError(
              `Invalid HTTP status "${response.status}".`
            );
            error.level = ErrorLevel.Warning;
            throw error;
          }
        })
    );
  }

  /**
   * Zformátuje procento v rozsahu [0.000, 1.000].
   * @param {?number} n
   * @private
   * @returns {string|null} Zformátované procento v rozsahu [0.000, 1.000] nebo null,
   * pokud je na vstupu null/undefined nebo číslo není konečné.
   */
  static formatPercent(n) {
    if (isNil(n) || !Number.isFinite(n)) {
      return null;
    }
    return n.toFixed(3);
  }

  /**
   * Vygeneruje Status Info "memory.*" sekci nebo základ pro "storage.fs.{mountPoint}.*" sekci.
   * @param {string} keyPrefix Vyescapovaný prefix klíčů.
   * @param {?number} used použito v B
   * @param {?number} total celkem v B
   * @private
   * @returns {StatusInfo|{}}
   */
  static generateMemory(keyPrefix, used, total) {
    if (!Number.isFinite(used) || !Number.isFinite(total)) {
      return {};
    }
    const res = {
      ...this.keyValueOptional(`${keyPrefix}.total`, total),
      ...this.keyValueOptional(`${keyPrefix}.used`, used)
    };
    if (total === 0) {
      return res;
    }
    return {
      ...res,
      ...this.keyValueOptional(
        `${keyPrefix}.use`,
        this.formatPercent(used / total)
      )
    };
  }

  /**
   * @param {Array.<NetworkConnection>} networkConnections
   * @private
   * @returns {StatusInfo}
   */
  static generateNetworkAdapters(networkConnections) {
    return networkConnections.reduce((acc, networkConnection) => {
      const adapter = {};
      const adapterPrefix = this.prefixNetworkAdapter(
        networkConnection.getMac()
      );
      const adapterType = this.mapNetworkAdapterType(
        networkConnection.getType()
      );
      if (!isNil(adapterType)) {
        adapter[`${adapterPrefix}.type`] = adapterType;
      }
      // Pozor, getIp může vyhodit výjimku, pokud není IP nastavena, proto test pomocí hasIp!
      if (networkConnection.hasIp()) {
        const ip = networkConnection.getIp();
        try {
          adapter[`${adapterPrefix}.ipv4.ip`] = new Address4(ip).correctForm();
        } catch {}
        try {
          adapter[`${adapterPrefix}.ipv6.ip`] = new Address6(ip).correctForm();
        } catch {}
      }
      return { ...acc, ...adapter };
    }, {});
  }

  /**
   * Vygeneruje Status Info "network.*" sekci.
   * @param {?Array.<NetworkConnection>} networkConnections
   * @private
   * @returns {StatusInfo|{}}
   */
  static generateNetwork(networkConnections) {
    if (isNil(networkConnections) || networkConnections.length === 0) {
      return {};
    }

    const networkAdapters = this.generateNetworkAdapters(networkConnections);

    const defaultConnection =
      this.selectDefaultNetworkConnectionWithIp(networkConnections);
    if (isNil(defaultConnection)) {
      return networkAdapters;
    }

    // Důvěřuj, ale prověřuj, jelikož NetworkConnection může v getIp vyhodit
    // výjimku, pokud by programátorskou chybou v algoritmu nebyla uvedena IP,
    // takže díky volitelnosti default sekce raději vrátíme nyní alespoň něco.
    if (!defaultConnection.hasIp()) {
      return networkAdapters;
    }

    const mac = defaultConnection.getMac();

    // TODO: Trochu nedomyšlenost specifikace, že dual IP stack zařízení
    // mohou mít dvě výchozí IP adresy, ale stejně NetworkConnection může
    // nyní nést jen jednu IP adresu neurčitého typu, takže to nevadí.
    // Nyní pro jednoduchost vybereme přednostně IPv4 před IPv6. Vždycky
    // musí vrátit nějakou IP, jinak se jedná o programátorský bug,
    // protože výše jsme si vyfiltrovali spojení s IP adresou.
    const adapterPrefix = this.prefixNetworkAdapter(mac);
    const ip =
      networkAdapters[`${adapterPrefix}.ipv4.ip`] ||
      networkAdapters[`${adapterPrefix}.ipv6.ip`];

    // Důvěřuj, ale prověřuj podruhé, pro robustnost...
    if (isNil(ip)) {
      return networkAdapters;
    }

    // Doplnění sekce "network.default.*" k "network.adapter.*".
    const defaultPrefix = 'network.default';
    return {
      ...networkAdapters,
      [`${defaultPrefix}.ip`]: ip,
      [`${defaultPrefix}.mac`]: mac
    };
  }

  /**
   * Převede všechny informace o zařízení do formátu Status Info.
   * @param {ReportInfo} reportInfo Informace o zařízení.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @private
   * @returns {StatusInfo} Status Info 1.3
   * @throws {Error} Hodnota nějakého povinného klíče/sekce nebyla k dispozici.
   */
  static generateReport(reportInfo, serverUrl) {
    // TODO: Nesloučit povinné a nepovinné položky/sekce dohromady?
    const { localControlLock, remoteControlLock } = reportInfo;
    return {
      'app.connection.type': 'light',
      ...this.keyValueOptional(
        'app.content.asset.pending',
        reportInfo.content?.asset?.pending
      ),
      ...this.keyValueMandatory('app.connection.url', serverUrl),
      'app.type': 'kiwi-player-light',
      ...this.keyValueMandatory(
        'app.type.kiwi-player-light.runtimePlatform',
        reportInfo.runtimePlatform
      ),
      'app.update.type': 'light',
      ...this.keyValueMandatory(
        'app.update.type.light.updatePlatform',
        UpdatePlatformUtil.generate(reportInfo.updatePlatform)
      ),
      ...this.keyValueMandatory('time.local', reportInfo.nowTimestamp),
      ...this.keyValueMandatory('time.zone', reportInfo.timezone),
      version: '1.3',

      // Nepovinné položky

      // Sekce app.*
      ...this.keyValueOptional(
        'app.type.kiwi-player-light.signage.firmware',
        reportInfo.firmwareVersion
      ),
      ...this.keyValueOptional(
        'app.update.type.light.buildId',
        reportInfo.buildId
      ),
      // TODO #504: Po refaktoringu zjišťování buildUrl zde navázat.
      // 'app.update.type.light.buildUrl'
      ...this.keyValueOptional('app.update.url', reportInfo.updateUrl),
      ...this.keyValueOptional(
        'app.updateTimestamp',
        reportInfo.contentTimestamp
      ),
      ...this.keyValueOptional('app.version', reportInfo.version),

      // Sekce control.*
      ...this.keyValueOptional(
        'control.local.exceptions.power',
        this.isControlPower(localControlLock)
      ),
      ...this.keyValueOptional(
        'control.local.locked',
        this.isControlLocked(localControlLock)
      ),
      ...this.keyValueOptional(
        'control.remote.exceptions.power',
        this.isControlPower(remoteControlLock)
      ),
      ...this.keyValueOptional(
        'control.remote.locked',
        this.isControlLocked(remoteControlLock)
      ),

      // Sekce memory.*
      ...this.generateMemory('memory', reportInfo.usedRam, reportInfo.totalRam),

      // Sekce network.*
      ...this.generateNetwork(reportInfo.networkConnections),

      // Klíč image (screenshot obrazovky)
      ...this.keyValueOptional('image', reportInfo.image),

      // Sekce screen.*
      ...this.generateScreen(reportInfo),

      // Sekce sensor.temperature.*
      ...this.generateSensorTemperature(reportInfo.temperature),

      // Sekce sound.*
      ...this.keyValueOptional('sound.muted', reportInfo.muted),
      ...this.keyValueOptional(
        'sound.volume',
        this.formatPercent(reportInfo.volume)
      ),

      // Sekce storage.*
      ...this.generateStorage(reportInfo.driveInfo),

      // Sekce system.*
      ...this.keyValueOptional('system.manufacturer', reportInfo.manufacturer),
      ...this.keyValueOptional('system.model', reportInfo.modelName),
      ...this.keyValueOptional('system.serial', reportInfo.serialNumber),

      // Sekce time.*
      ...this.keyValueOptional('time.boot', reportInfo.bootTimestamp)
    };
  }

  /**
   * Vygeneruje Status Info "screen.*" sekci.
   * @param {ReportInfo} reportInfo
   * @private
   * @returns {StatusInfo|{}}
   */
  static generateScreen(reportInfo) {
    // Vrací čísla ve shodě se specifikací, takže nemusíme mapovat.
    const orientation = CommunicatorOrientation.generate(
      reportInfo.orientationEnum
    );
    if (isNil(orientation) && isNil(reportInfo.brightness)) {
      return {};
    }
    const screenPrefix = 'screen.0';
    return {
      ...this.keyValueOptional(
        `${screenPrefix}.brightness`,
        this.formatPercent(reportInfo.brightness)
      ),
      ...this.keyValueOptional(`${screenPrefix}.orientation`, orientation),
      ...this.keyValueOptional(`${screenPrefix}.primary`, true)
    };
  }

  /**
   * Vygeneruje Status Info "storage.*" sekci.
   * @param {ReportFileSystemUnitInfo} reportFileSystemUnitInfo
   * @private
   * @returns {StatusInfo|{}}
   */
  static generateStorage(reportFileSystemUnitInfo) {
    if (isNil(reportFileSystemUnitInfo)) {
      return {};
    }
    return reportFileSystemUnitInfo.fileSystemUnitInfoArray.reduce(
      (acc, fileSystemUnit) => {
        const keyPrefix = `storage.fs.${this.encodeKeyComponent(
          fileSystemUnit.id
        )}`;
        if (!isNil(fileSystemUnit.type)) {
          return {
            ...acc,
            ...this.generateMemory(
              keyPrefix,
              fileSystemUnit.used,
              fileSystemUnit.total
            ),
            ...this.keyValueOptional(
              `${keyPrefix}.type`,
              this.mapStorageFsType(fileSystemUnit.type)
            )
          };
        }
        return {
          ...acc,
          ...this.generateMemory(
            keyPrefix,
            fileSystemUnit.used,
            fileSystemUnit.total
          )
        };
      },
      {
        ...this.keyValueOptional(
          'storage.types',
          // Naprsuje storage string do a následně výsledek převede na řetezec.
          // To se dělá kvůli tomu, aby se odeslaly pouze validní hodnoty.
          StorageTypesParser.toString(
            StorageTypesParser.parse(reportFileSystemUnitInfo.type)
          ) ?? config.storage
        )
      }
    );
  }

  /**
   * Vygeneruje Status Info "sensor.temperature.*" sekci.
   * @param {?Celsius} temperature
   * @private
   * @returns {StatusInfo|{}}
   */
  static generateSensorTemperature(temperature) {
    if (isNil(temperature)) {
      return {};
    }
    // Máme 1 senzor teploty, proto pevný index 0.
    const keyPrefix = 'sensor.temperature.0';
    return {
      ...this.keyValueOptional(`${keyPrefix}.value`, temperature),
      [`${keyPrefix}.type`]: 'system'
    };
  }

  /**
   * Převede zámek ovládání do příznaku `locked`, který se posílá na server.
   * Pokud zámek chybí tak do reportu nevloží nic (např. zařízení nepodporuje).
   * Pokud je zámek přítomný (zařízení omezení podporuje), tak je vždy `locked`
   * nastaveno na `true` nebo `false` dle stavu uzamčení.
   * @param {?ControlLock} controlLock
   * @private
   * @returns {?boolean}
   */
  static isControlLocked(controlLock) {
    return get(controlLock, 'locked', null);
  }

  /**
   * Převede zámek ovládání do příznaku `power`, který se posílá na server.
   * Příznak `power` se odesílá pouze v případě, že je nastaven na `true`.
   * @param {?ControlLock} controlLock
   * @private
   * @returns {?boolean}
   */
  static isControlPower(controlLock) {
    const power = get(controlLock, 'exceptions.power');
    if (power) {
      return true;
    }
    return null;
  }

  /**
   * Vytvoří objekt s klíčem key a primitivní hodnotou zformátovanou pomocí JSON.stringify(value)
   * nebo vrátí prázdný objekt, pokud je value null nebo undefined.
   * @param {string} key
   * @param {boolean|number|string|null|undefined} value
   * @private
   * @returns {StatusInfo|{}} Objekt s jedním klíčem a string hodnotou nebo prázdný objekt.
   */
  static keyValueOptional(key, value) {
    if (isNil(value)) {
      return {};
    }
    let stringValue = value;
    if (!isString(value)) {
      stringValue = JSON.stringify(value);
    }
    return { [`${key}`]: stringValue };
  }

  /**
   * Vytvoří objekt s klíčem key a primitivní hodnotou zformátovanou pomocí JSON.stringify(value)
   * nebo vyhodí výjimku, pokud je value null nebo undefined.
   * @param {string} key
   * @param {boolean|number|string|null|undefined} value
   * @private
   * @returns {StatusInfo} Objekt s jedním klíčem a string hodnotou.
   * @throws {Error} Pokud je value null nebo undefined.
   */
  static keyValueMandatory(key, value) {
    if (isNil(value)) {
      throw new Error(`Invalid null value for mandatory item "${key}"`);
    }
    return this.keyValueOptional(key, value);
  }

  /**
   * Namapuje NetworkType na hodnoty podle specifikace pro network.adapter.{mac}.type.
   * @param {NetworkType} networkType
   * @private
   * @returns {?string}
   */
  static mapNetworkAdapterType(networkType) {
    switch (networkType) {
      case NetworkType.Ethernet:
        return 'wired';
      case NetworkType.Wifi:
        return 'wifi';
      default:
        return null;
    }
  }

  /**
   * Namapuje FileSystemUnit na hodnoty podle specifikace pro storage.fs.{mountPoint}.type.
   * @param {FileSystemUnit} fileSystemUnit
   * @private
   * @returns {?string}
   */
  static mapStorageFsType(fileSystemUnit) {
    switch (fileSystemUnit) {
      case FileSystemType.Internal:
        return 'internal';
      case FileSystemType.Usb:
        return 'external';
      case FileSystemType.SdCard:
        return 'external';
      // TODO: Ve specifikaci je ještě 'network', ale pro to nemáme hodnotu pro FileSystemType.
      default:
        return null;
    }
  }

  /**
   * TODO #506: Pozor, že MAC adresa z NetworkConnection
   * nyní nemusí být normalizovaná, ale předpokládáme, že má potřebný
   * formát lower-case po dvojicích oddělené dvojtečkou, jak vyžaduje
   * specifikace Status Info.
   * @param {string} mac
   * @private
   * @returns {string} Prefix klíče pro daný adaptér ve Status Info.
   */
  static prefixNetworkAdapter(mac) {
    return `network.adapter.${this.encodeKeyComponent(mac)}`;
  }

  /**
   * Vybere maximálně jeden síťový adaptér s IP adresou.
   * @param {Array.<NetworkConnection>} networkConnections
   * @private
   * @returns {?NetworkConnection}
   */
  static selectDefaultNetworkConnectionWithIp(networkConnections) {
    const withIpConnections = networkConnections.filter((networkConnection) =>
      networkConnection.hasIp()
    );

    // Sice alibismus, ale při volitelnosti default sekce a nemožnosti určení
    // výchozí IP bez znalosti routovacích tabulek s metrikami je to vlastně
    // správné vracet default sekci jen při právě jednom adaptéru s IP adresou.
    if (withIpConnections.length === 1) {
      return withIpConnections[0];
    }
    return null;
  }
}

export default CommunicatorReport;
